import type { ModalSettings, ModalStore, ToastStore } from '@skeletonlabs/skeleton'

import { createClientId } from '@utils/client-id'

type Restorable = {
  settings: ModalSettings
  restoreKey: string
}

const restoreCache = new Map<string, unknown>()
let lastRestorable: Restorable | null = null

const setRestoreState = (restoreKey: string, state: unknown) => {
  try {
    // Prefer structuredClone if available to avoid proxy artifacts
    const snapshot =
      typeof structuredClone === 'function'
        ? structuredClone(state)
        : JSON.parse(JSON.stringify(state))
    restoreCache.set(restoreKey, snapshot)
  } catch {
    // Best-effort fallback: store as-is
    restoreCache.set(restoreKey, state)
  }
}

const clearRestoreState = (restoreKey?: string) => {
  if (restoreKey) {
    restoreCache.delete(restoreKey)
  }
  lastRestorable = null
}

const prepareRestorableModal = (
  modalStore: ModalStore,
  toastStore: ToastStore,
  settings: ModalSettings,
  options?: { toastMessage?: string; toastTimeoutMs?: number },
): ModalSettings => {
  const restoreKey = createClientId()
  const toastMessage = options?.toastMessage ?? 'Modal closed. Reopen to restore your changes?'
  const toastTimeoutMs = options?.toastTimeoutMs ?? 5000

  const originalResponse = settings.response
  const meta = { ...(settings.meta ?? {}), restoreKey }
  const restorableSettings: ModalSettings = { ...settings, meta, response: undefined }

  restorableSettings.response = async (r: unknown) => {
    if (r === undefined) {
      lastRestorable = { settings: restorableSettings, restoreKey }
      const id = toastStore.trigger({
        message: toastMessage,
        background: 'variant-filled-surface',
        autohide: true,
        timeout: toastTimeoutMs,
        action: {
          label: 'Reopen',
          response: () => {
            const restoreState = restoreCache.get(restoreKey)
            if (lastRestorable) {
              const reopen: ModalSettings = {
                ...lastRestorable.settings,
                meta: { ...(lastRestorable.settings.meta ?? {}), restoreState },
              }
              modalStore.trigger(reopen)
            }
            clearRestoreState(restoreKey)
          },
        },
      })
      // Ensure we clear the reference after the toast vanishes
      setTimeout(() => {
        if (lastRestorable?.restoreKey === restoreKey) {
          clearRestoreState(restoreKey)
        }
        // Attempt to close by id if still present
        try {
          if (id !== undefined && id !== null) {
            toastStore.close(id)
          }
        } catch {
          /* noop */
        }
      }, toastTimeoutMs + 100)
      return
    }
    try {
      return await originalResponse?.(r)
    } finally {
      clearRestoreState(restoreKey)
    }
  }

  return restorableSettings
}

export { clearRestoreState, prepareRestorableModal, setRestoreState }
